import { Dispatch } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ChakraBox } from '@equip.health/ui';

import SectionHeader from '~/components/common/SectionHeader';
import OverdueAssessmentsSection from '~/components/common/OverdueAssessmentsSection/OverdueAssessmentsSection';
import { OverdueAssessmentsSectionErrorFallback } from '~/components/common/OverdueAssessmentsSection/OverdueAssessmentsSectionErrorFallback';
import {
  AssessmentAction,
  PatientTabs,
} from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { AssessmentEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

import CareTeamOverviewV2 from './CareTeamOverviewV2';
import { NoteSummariesOverview } from './NoteSummariesOverview';
import SafetyPlanOverview from './SafetyPlanOverview';

interface PatientOverviewRightRailProps {
  patientFirstName: string;
  patientId: string;
  patientLastName: string;
  updateAssessmentState: Dispatch<AssessmentAction>;
}

const PatientOverviewRightRail = ({
  patientFirstName,
  patientId,
  patientLastName,
  updateAssessmentState,
}: PatientOverviewRightRailProps) => {
  const { setComponentVisibility } = useComponentPortalContext();
  const { trackEvent } = useAnalytics();

  return (
    <>
      <ChakraBox marginBottom="16px">
        <ErrorBoundary fallback={<OverdueAssessmentsSectionErrorFallback />}>
          <OverdueAssessmentsSection
            onClickCompleteTogether={(surveyResponse) => {
              trackEvent(
                AssessmentEvents.completeTogetherAssessmentCardOverdueButtonClicked,
                {
                  [AssessmentEventAttribute.SurveyId]:
                    surveyResponse.surveyExternalId,
                  [AssessmentEventAttribute.SurveyName]:
                    surveyResponse.surveyName,
                  [AssessmentEventAttribute.Source]:
                    AssessmentEventSource.PatientOverviewTab,
                  [AssessmentEventAttribute.ResponseExternalId]:
                    surveyResponse.responseExternalId,
                  [AssessmentEventAttribute.UserId]: surveyResponse.userId,
                },
              );
              setComponentVisibility({
                component: PortalComponent.SURVEY_ACCORDION,
                data: surveyResponse,
                isExpanded: true,
                isVisible: true,
              });
            }}
            patientId={patientId}
          />
        </ErrorBoundary>
      </ChakraBox>
      <ChakraBox marginBottom="16px">
        <NoteSummariesOverview patientId={patientId} />
      </ChakraBox>
      <ChakraBox marginBottom="16px">
        <SectionHeader
          action={{
            linkTo: PatientTabsCollection[PatientTabs.ASSESSMENTS].subPath,
            patientId,
            rightLinkText: 'View assessments',
          }}
          headerText="Safety plan"
        />
        <SafetyPlanOverview
          patientFullName={getPreferredFullName(
            null,
            patientFirstName,
            patientLastName,
          )}
          patientId={patientId}
          updateAssessmentState={updateAssessmentState}
        />
      </ChakraBox>
      <ChakraBox marginBottom="16px">
        <SectionHeader
          action={{
            linkTo: PatientTabsCollection[PatientTabs.CARE_PLAN].subPath,
            patientId,
            rightLinkText: 'View care plan',
          }}
          headerText="Provider Team"
        />
        <CareTeamOverviewV2 patientId={patientId} />
      </ChakraBox>
    </>
  );
};

export default PatientOverviewRightRail;
